import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Feather, Library, Sparkles } from "lucide-react";

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="container-wrap pt-16 md:pt-24 pb-20 md:pb-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <p className="overline mb-5"><span className="gold-divider align-middle mr-3" />New Orleans Literacy Nonprofit</p>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-brand-dark leading-[0.95] mb-6">
              Reading with <em className="text-brand-purple not-italic">purpose.</em><br/>
              Writing the <span className="text-brand-gold">future.</span>
            </h1>
            <p className="text-base md:text-lg text-brand-dark/75 max-w-xl leading-relaxed mb-8">
              We put books in kids' hands — and power in their voices. Through author-led reading sessions and our Young Authors program, we help children ages 5 to 12 fall in love with reading and become published authors themselves.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/donate" className="btn-primary" data-testid="hero-donate-button">
                Donate Now <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/about" className="btn-secondary" data-testid="hero-learn-more-button">
                Our Story
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="absolute -top-6 -left-6 w-44 h-44 bg-brand-lavender rounded-full blur-3xl opacity-70" />
            <div className="relative rounded-3xl overflow-hidden shadow-xl shadow-brand-purple/20 ring-1 ring-brand-purple/10">
              <img
                src="https://images.pexels.com/photos/14025659/pexels-photo-14025659.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
                alt="Children reading together"
                className="w-full h-[460px] object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -right-2 bg-white rounded-2xl shadow-xl px-5 py-4 border border-brand-gold/30 hidden md:flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-brand-gold" />
              <div>
                <p className="font-serif-display text-2xl text-brand-dark leading-none">1000+</p>
                <p className="text-xs text-brand-dark/60">Books distributed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT STRIP */}
      <section className="bg-brand-purple text-white py-12">
        <div className="container-wrap grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: "1,000+", l: "Free books distributed" },
            { n: "5–12", l: "Ages served" },
            { n: "2", l: "Core programs" },
            { n: "1", l: "Library inside a barbershop" },
          ].map((s, i) => (
            <div key={i}>
              <p className="font-serif-display text-4xl md:text-5xl text-brand-gold">{s.n}</p>
              <p className="text-sm uppercase tracking-wider text-white/80 mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="container-wrap py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
          <div className="md:col-span-7">
            <p className="overline mb-4"><span className="gold-divider align-middle mr-3" />Our Programs</p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-brand-dark leading-tight">
              Two pathways to a lifetime of stories.
            </h2>
          </div>
          <p className="md:col-span-5 text-brand-dark/70 leading-relaxed">
            Designed for young minds in underserved communities, our programs build the skills, voice, and confidence kids need to thrive.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="group relative rounded-3xl bg-white border border-brand-purple/15 p-8 md:p-10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all">
            <div className="w-14 h-14 rounded-2xl bg-brand-lavender flex items-center justify-center mb-6">
              <BookOpen className="w-7 h-7 text-brand-purple" />
            </div>
            <h3 className="font-serif-display text-3xl text-brand-dark mb-3">Reading Literacy Program</h3>
            <p className="text-brand-dark/70 leading-relaxed mb-6">
              Foundational reading skills, comprehension, and vocabulary development through engaging, culturally relevant materials and author-led sessions.
            </p>
            <p className="text-brand-purple text-sm font-semibold uppercase tracking-wider">Ages 5–12</p>
          </div>

          <div className="group relative rounded-3xl bg-brand-dark text-white p-8 md:p-10 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all">
            <div className="w-14 h-14 rounded-2xl bg-brand-gold/20 flex items-center justify-center mb-6">
              <Feather className="w-7 h-7 text-brand-gold" />
            </div>
            <h3 className="font-serif-display text-3xl text-white mb-3">Young Authors Program</h3>
            <p className="text-white/80 leading-relaxed mb-6">
              We guide kids step-by-step on how to structure, write, and illustrate their own books — and become published authors with their name on the cover.
            </p>
            <p className="text-brand-gold text-sm font-semibold uppercase tracking-wider">Flagship Program</p>
          </div>
        </div>
      </section>

      {/* BARBERSHOP LIBRARY FEATURE */}
      <section className="container-wrap pb-24">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 relative">
            <img
              src="https://images.pexels.com/photos/4969838/pexels-photo-4969838.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
              alt="WHO DAT BarberShop library"
              className="rounded-3xl shadow-xl shadow-brand-purple/15 w-full h-[520px] object-cover"
            />
            <div className="absolute top-6 left-6 bg-brand-gold text-brand-dark text-xs uppercase tracking-widest font-bold px-4 py-2 rounded-full">
              Read With Me Book Club
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="overline mb-4"><span className="gold-divider align-middle mr-3" />The Library</p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-brand-dark leading-tight mb-6">
              A library inside a barbershop — because access shouldn't have walls.
            </h2>
            <p className="text-brand-dark/70 leading-relaxed mb-4">
              Our flagship community library lives inside the WHO DAT BarberShop in New Orleans. Kids can stop in for a haircut and leave with a stack of books — for free.
            </p>
            <p className="text-brand-dark/70 leading-relaxed mb-8">
              It's a safe space, a quiet corner, and a launching pad for young readers and writers in our neighborhood.
            </p>
            <div className="flex items-center gap-3">
              <Library className="w-6 h-6 text-brand-gold" />
              <span className="text-brand-dark font-medium">Open during shop hours. Always free for children.</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-wrap pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-brand-purple to-brand-purple-deep text-white p-10 md:p-16 grid md:grid-cols-12 gap-8 items-center relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-gold/20 blur-3xl" />
          <div className="md:col-span-8 relative">
            <h2 className="font-serif-display text-4xl md:text-5xl mb-3">Your gift puts books in tiny hands.</h2>
            <p className="text-white/80 max-w-2xl">Every $25 sponsors a new reader. Every $100 helps a child become a published author.</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end relative">
            <Link to="/donate" className="inline-flex items-center bg-brand-gold text-brand-dark hover:bg-brand-gold-soft px-7 py-4 rounded-full font-semibold transition hover:-translate-y-0.5" data-testid="cta-donate-button">
              Donate Today <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
